import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Filter, List, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import Button from 'v3/components/Button';
import { GROUPS, PER_PAGE, formatDate, formatMoney, readPages } from './contract';
import useKanban, { changedGroups } from './useKanban';
import KanbanCard from './Card';
import KanbanTabs from './Tabs';
import {
  DEFAULT_FILTERS,
  normalizeFilters,
  serializedFilters,
  filterCount,
} from './Filters/contract';
import { readSavedFilters, saveFilters } from './Filters/savedFilters';
import { validatePermission } from 'actions';
import { canReadCandidates } from './Candidates/contract';
import useAvailableVehicleCounts from './useAvailableVehicleCounts';
import './styles.css';

/** As próximas tasks conectam componentes de listas, filtros e veículos por esta interface. */
export default function KanbanWorkspace({
  companyId,
  canView,
  canViewPreLoads,
  integrations = {},
  rowIntegrations = {},
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const lastCompany = useRef(companyId);
  const [queryCompany, setQueryCompany] = useState(companyId);
  const changedCompany = queryCompany !== companyId;
  const rawFilters = changedCompany ? null : searchParams.get('filters');
  // Sem ?filters=, valem os filtros salvos do usuário para a empresa; sem registro, 7 dias.
  // Sem empresa não há registro a ler nem gravar: vale o padrão.
  const hasCompany = companyId !== null && companyId !== undefined;
  const [saved, setSaved] = useState({ companyId: undefined, value: null });
  const [saveError, setSaveError] = useState(null);
  const needsSaved = canView && hasCompany && !rawFilters && saved.companyId !== companyId;
  useEffect(() => {
    if (!needsSaved) return undefined;
    const abort = new AbortController();
    const resolve = (value) => {
      if (!abort.signal.aborted) setSaved({ companyId, value: value || DEFAULT_FILTERS });
    };
    readSavedFilters(companyId, abort.signal).then(resolve, () => resolve(null));
    return () => abort.abort();
  }, [companyId, needsSaved]);
  const filterState = useMemo(() => {
    if (!rawFilters)
      return !hasCompany
        ? { value: DEFAULT_FILTERS, error: null, pending: false }
        : saved.companyId === companyId
          ? { value: saved.value, error: null, pending: false }
          : { value: DEFAULT_FILTERS, error: null, pending: true };
    try {
      return { value: normalizeFilters(rawFilters), error: null, pending: false };
    } catch (error) {
      return { value: DEFAULT_FILTERS, error: error.message, pending: false };
    }
  }, [rawFilters, saved, companyId, hasCompany]);
  const filtersKey = serializedFilters(filterState.value);
  const pages = readPages(searchParams);
  const {
    groups,
    loading: queryLoading,
    loadingGroups,
    error: queryError,
    refresh,
  } = useKanban({
    companyId,
    enabled: canView && !changedCompany && !filterState.error && !filterState.pending,
    pages,
    filtersKey,
  });
  const loading = queryLoading || filterState.pending;
  const vehicleCounts = useAvailableVehicleCounts({
    companyId,
    group: groups?.AVAILABLE,
    enabled:
      canView &&
      !!integrations.vehicles &&
      !loadingGroups.AVAILABLE &&
      canReadCandidates(validatePermission),
  });
  // Ao aplicar filtros, só as colunas da seção alterada recarregam.
  const columnLoading = (code) => filterState.pending || loadingGroups[code];
  const error = filterState.error || queryError;
  const [active, setActive] = useState(null);
  const trigger = useRef(null);
  useEffect(() => {
    if (lastCompany.current === companyId) return;
    lastCompany.current = companyId;
    setActive(null);
    setSaveError(null);
    setSearchParams({}, { replace: true, state: { ...location.state, kanbanFilterLabels: null } });
  }, [companyId, location.state, setSearchParams]);
  useEffect(() => {
    if (changedCompany && !location.search) setQueryCompany(companyId);
  }, [changedCompany, companyId, location.search]);
  // Volta do atendimento: reabre o modal da mesma carga e tira o pedido do histórico, para que
  // atualizar a página não o reabra de novo.
  const reopen = location.state?.kanbanReopen;
  useEffect(() => {
    if (!reopen || reopen.companyId !== companyId) return;
    const { kanbanReopen, ...state } = location.state;
    navigate(`${location.pathname}${location.search}`, { replace: true, state });
    if (!integrations[reopen.kind] || !reopen.item) return;
    setActive({
      kind: reopen.kind,
      companyId,
      group: reopen.group,
      item: reopen.item,
      restore: reopen.candidates ? { ...reopen.candidates, candidateId: reopen.candidateId } : null,
      location: { pathname: location.pathname, search: location.search },
    });
  }, [reopen, companyId, integrations, location, navigate]);
  function setPage(code, page) {
    const params = new URLSearchParams(searchParams);
    if (page === 1) params.delete(`p_${code}`);
    else params.set(`p_${code}`, String(page));
    setSearchParams(params, { state: location.state });
  }
  function open(kind, group = null, item = null, options = null) {
    if (!integrations[kind]) return;
    trigger.current = document.activeElement;
    setActive({
      ...options,
      kind,
      companyId,
      group,
      item,
      query: { pages, perPage: PER_PAGE, filters: filterState.value },
      location: { pathname: location.pathname, search: location.search },
    });
  }
  function close(changed = false) {
    setActive(null);
    if (changed) refresh();
    requestAnimationFrame(() => trigger.current?.focus());
  }
  function applyFilters(filters, labels) {
    const params = new URLSearchParams(searchParams);
    params.set('filters', serializedFilters(filters));
    // Só as colunas da seção alterada (Cargas ou Viagens) voltam para a primeira página.
    for (const code of changedGroups(filterState.value, filters)) params.delete(`p_${code}`);
    setSearchParams(params, {
      state: { ...location.state, kanbanFilterLabels: { companyId, labels } },
    });
    setSaved({ companyId, value: filters });
    setSaveError(null);
    if (hasCompany)
      saveFilters(companyId, filters).catch(() =>
        setSaveError(
          'Não foi possível salvar os filtros para os próximos acessos. Eles valem nesta consulta.',
        ),
      );
    close();
  }
  function returnToLoads() {
    const origin = location.state?.kanbanOrigin;
    const path = canViewPreLoads ? '/pre-cargas' : '/cargas';
    const search =
      origin?.path === path && typeof origin.search === 'string' && origin.search.startsWith('?')
        ? origin.search
        : '';
    navigate(`${path}${search}`);
  }
  if (!canView)
    return (
      <div className="tc-k-root">
        <p role="alert">Você não tem permissão para consultar o Kanban.</p>
      </div>
    );
  const current = active?.companyId === companyId ? active : null;
  const View = current ? integrations[current.kind] : null;
  const overlay = current && ['filters', 'vehicles'].includes(current.kind);
  const viewGroups = current && !overlay && (loading || error) ? null : groups;
  // Abas só nas listas (5630): o quadro fica como antes, e a aba KANBAN volta aos cards.
  const activeTab = current && !overlay ? (current.kind === 'trips' ? 'TRIPS' : current.group) : null;
  return (
    <div className="tc-k-root">
      {activeTab && (
        <KanbanTabs
          active={activeTab}
          disabled={(tab) =>
            !integrations[tab.kind] ||
            (tab.key === 'TRIPS' ? loading : columnLoading(tab.key)) ||
            !!error
          }
          onSelect={(tab) =>
            tab.key === 'KANBAN' ? close() : open(tab.kind, tab.key === 'TRIPS' ? null : tab.key)
          }
        />
      )}
      <div hidden={!!View && !overlay}>
        <header className="tc-k-toolbar">
          <h1>Kanban — Torre de Controle</h1>
          <div className="tc-k-actions">
            <Button variant="secondary" size="sm" onClick={returnToLoads}>
              <ChevronLeft size={15} aria-hidden="true" />
              {canViewPreLoads ? 'Pré Cargas' : 'Cargas'}
            </Button>
            <Button
              variant="success"
              size="sm"
              disabled={!integrations.filters}
              title={integrations.filters ? 'Filtrar cargas e viagens' : 'Indisponível no momento'}
              onClick={() => open('filters')}
            >
              <Filter size={15} aria-hidden="true" />
              Filtros
              {filterCount(filterState.value) > 0 && (
                <span aria-label={`${filterCount(filterState.value)} campos filtrados`}>
                  {' '}
                  ({filterCount(filterState.value)})
                </span>
              )}
            </Button>
            <Button
              size="sm"
              disabled={!integrations.trips || loading || !!error}
              title={integrations.trips ? 'Lista de viagens' : 'Indisponível no momento'}
              onClick={() => open('trips')}
            >
              <List size={15} aria-hidden="true" />
              Listar viagens
            </Button>
            <Button variant="secondary" size="sm" disabled={loading} onClick={refresh}>
              <RefreshCw size={15} aria-hidden="true" />
              Atualizar
            </Button>
          </div>
        </header>
        <p className="tc-k-timezone">
          Horários de Brasília · Atualize a consulta após alterações no atendimento.
        </p>
        {saveError && (
          <p className="tc-k-warning" role="status">
            {saveError}
          </p>
        )}
        {error && (
          <div className="tc-k-error" role="alert">
            <span>{error}</span>
            <Button size="sm" onClick={refresh}>
              Tentar novamente
            </Button>
          </div>
        )}
        <div
          className="tc-k-scroll"
          role="region"
          aria-label="Quatro colunas do Kanban"
          tabIndex={0}
          aria-busy={loading}
        >
          <div className="tc-k-board">
            {GROUPS.map((definition) => {
              const group = groups?.[definition.code];
              return (
                <section
                  className="tc-k-column"
                  key={definition.code}
                  data-group={definition.code}
                  aria-labelledby={`tc-k-${definition.code}`}
                  style={{ '--tc-stage-color': definition.color }}
                >
                  <header className="tc-k-column-header">
                    <div className="tc-k-column-title">
                      <h2 id={`tc-k-${definition.code}`}>{definition.label}</h2>
                      <Button
                        className="tc-k-list"
                        size="xs"
                        disabled={
                          !integrations[definition.integration] ||
                          columnLoading(definition.code) ||
                          !!error
                        }
                        aria-label={`Lista ${definition.label}`}
                        title={
                          integrations[definition.integration]
                            ? `Lista ${definition.label}`
                            : 'Indisponível no momento'
                        }
                        onClick={() => open(definition.integration, definition.code)}
                      >
                        <List size={13} aria-hidden="true" />
                      </Button>
                    </div>
                    <div className="tc-k-totals">
                      <strong>
                        {!group
                          ? '—'
                          : group.amountComplete
                            ? formatMoney(group.totalAmount)
                            : 'Total não informado'}
                      </strong>
                      <span
                        className="tc-k-count"
                        aria-label={`${definition.label}: ${group ? `${group.totalCount} registros` : 'quantidade não consultada'}`}
                      >
                        {group?.totalCount ?? '—'}
                      </span>
                    </div>
                    {group && !group.amountComplete && (
                      <p className="tc-k-incomplete">
                        Subtotal conhecido: {formatMoney(group.knownAmount)} ·{' '}
                        {group.missingAmountCount} sem valor em reais
                      </p>
                    )}
                  </header>
                  <div
                    className="tc-k-cards"
                    tabIndex={0}
                    role="region"
                    aria-label={`Cards ${definition.label}`}
                    key={`${definition.code}-${pages[definition.code]}`}
                  >
                    {columnLoading(definition.code) && (
                      <p role="status" className="tc-k-empty">
                        Carregando {definition.label.toLowerCase()}…
                      </p>
                    )}
                    {!columnLoading(definition.code) && !error && group?.data.length === 0 && (
                      <p role="status" className="tc-k-empty">
                        {group.totalCount
                          ? 'Nenhum registro nesta página.'
                          : 'Nenhum registro neste grupo.'}
                      </p>
                    )}
                    {group?.data.map((item) => (
                      <KanbanCard
                        key={item.key}
                        item={item}
                        vehiclesAvailable={!!integrations.vehicles}
                        vehicleCount={
                          definition.code === 'AVAILABLE' ? vehicleCounts.get(item.loadId) : undefined
                        }
                        onVehicles={(selected) => open('vehicles', definition.code, selected)}
                        onOther={(selected) =>
                          open('vehicles', definition.code, selected, { openOther: true })
                        }
                      />
                    ))}
                  </div>
                  {group && (
                    <footer className="tc-k-pagination">
                      <div>
                        <Button
                          size="xs"
                          variant="secondary"
                          disabled={columnLoading(definition.code) || pages[definition.code] <= 1}
                          aria-label={`Página anterior: ${definition.label}`}
                          onClick={() => setPage(definition.code, pages[definition.code] - 1)}
                        >
                          <ChevronLeft size={16} aria-hidden="true" />
                        </Button>
                        <span>
                          Página {pages[definition.code]} de {group.pagination.totalPages || 1}
                        </span>
                        <Button
                          size="xs"
                          variant="secondary"
                          disabled={columnLoading(definition.code) || !group.pagination.hasMore}
                          aria-label={`Próxima página: ${definition.label}`}
                          onClick={() => setPage(definition.code, pages[definition.code] + 1)}
                        >
                          <ChevronRight size={16} aria-hidden="true" />
                        </Button>
                      </div>
                      <small>Consulta: {formatDate(group.asOf)}</small>
                    </footer>
                  )}
                </section>
              );
            })}
          </div>
        </div>
      </div>
      {View && (
        <View
          context={{
            ...current,
            query: { pages, perPage: PER_PAGE, filters: filterState.value },
            filterLabels:
              location.state?.kanbanFilterLabels?.companyId === companyId
                ? location.state.kanbanFilterLabels.labels
                : {},
          }}
          queryState={{ groups: viewGroups, loading, error }}
          rowIntegrations={rowIntegrations}
          onPageChange={(page) => setPage(current.group, page)}
          onRefresh={refresh}
          onClose={() => close()}
          onChanged={() => close(true)}
          onApplyFilters={applyFilters}
        />
      )}
    </div>
  );
}
